import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { extractSystemId } from "utils/utils";
import {
  AlarmPageStrings,
  ALARMS_TABLE_STRINGS,
  dialogStrings,
  InputErrorStrings,
  Tag_Type_Strings,
  TagPageStrings,
  valueStrings
} from "constants/constantStrings";

import DupontDialog from "components/DupontDialog";
import InputBox from "components/InputBox";

import "./AddTagAlarmModal.scss";

const INPUT_MINIMUM = 3;

const AddTagAlarmModal = props => {
  const { systemsAsObj } = useSelector(state => state.systems);

  const { isAddAlarm, handleClose, Tags } = props;

  const tagIdSet = useMemo(() => {
    if (!Array.isArray(Tags)) return new Set();
    return new Set(
      Tags.map(tag => {
        if (isAddAlarm) {
          return (tag.AlarmID || "").trim().toLowerCase();
        }
        return (tag.TagId || "").trim().toLowerCase();
      })
    );
  }, [Tags, isAddAlarm]);
  const typeCodeMap = {
    standard: "T",
    normalized: "N",
    setpoints: "S",
    alarm: "A"
  };

  const tagTypeOptions = [
    { value: "standard", label: Tag_Type_Strings.standard },
    { value: "setpoints", label: Tag_Type_Strings.setpoints },
    { value: "normalized", label: Tag_Type_Strings.normalized }
  ];

  const [formData, setFormData] = useState({ TagType: isAddAlarm ? typeCodeMap.alarm : typeCodeMap.standard });
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [tagIdError, setTagIdError] = useState(null);

  const systemID = extractSystemId();
  const { systemName = "" } = systemsAsObj[systemID] || {};
  const [tagDescriptionError, setTagDescriptionError] = useState(null);
  const [tagNameError, setTagNameError] = useState(null);
  const [unitError, setUnitError] = useState(null);

  const getFieldError = (srcData, field) => {
    const fieldVal = srcData[field];
    if (!fieldVal || fieldVal.trim().length === 0) return InputErrorStrings.required;
    if (field !== "Units" && fieldVal.trim().length < INPUT_MINIMUM) return InputErrorStrings.minimumRequired;
    if (field === "TagId") {
      if (isAddAlarm && !fieldVal.toUpperCase().startsWith("ALARM")) return InputErrorStrings.alarmPrefix;
      const trimmedValue = fieldVal.trim().toLowerCase();
      if (tagIdSet.has(trimmedValue)) return isAddAlarm ? InputErrorStrings.duplicateAlarmId : InputErrorStrings.duplicateTagId;
    }
    return null;
  };

  const onInputChange = (value, name) => {
    const updatedData = { ...formData, [name]: value };
    switch (name) {
      case "TagName":
        setTagNameError(getFieldError(updatedData, "TagName"));
        break;
      case "Description":
        setTagDescriptionError(getFieldError(updatedData, "Description"));
        break;
      case "TagId":
        setTagIdError(getFieldError(updatedData, "TagId"));
        break;
      case "Units":
        if (!isAddAlarm) setUnitError(getFieldError(updatedData, "Units"));
        break;
      default:
        break;
    }

    const isTagNameInValid = !!getFieldError(updatedData, "TagName");
    const isDescritptionInValid = !!getFieldError(updatedData, "Description");
    const isTagIdInValid = !!getFieldError(updatedData, "TagId");
    const isUnitInvalid = !isAddAlarm ? !!getFieldError(updatedData, "Units") : false;
    const isDisabled =
      isTagNameInValid ||
      isDescritptionInValid ||
      isTagIdInValid ||
      (!isAddAlarm && (isUnitInvalid || !updatedData.Units || updatedData.Units.trim().length === 0));
    setFormData(updatedData);
    setConfirmDisabled(isDisabled);
  };

  const onMaxLengthReachedDescription = maxLength => setTagDescriptionError(InputErrorStrings.maxlimitis + maxLength);
  const onMaxLengthReachedTag = maxLength => setTagNameError(InputErrorStrings.maxlimitis + maxLength);
  const onMaxLengthReachedTagId = maxLength => setTagIdError(InputErrorStrings.maxlimitis + maxLength);
  const onMaxLengthReachedUnit = maxLength => setUnitError(InputErrorStrings.maxlimitis + maxLength);

  const [radioValue, setRadioValue] = useState(isAddAlarm ? "alarm" : "standard");

  const handleRadioChange = value => {
    setRadioValue(value);
    setFormData(prev => ({ ...prev, TagType: typeCodeMap[value] }));
  };

  const onFormSubmit = async data => {
    if (data === dialogStrings.confirm) {
      const cleanedFormData = {
        ...formData,
        TagId: formData.TagId ? formData.TagId.trim() : formData.TagId
      };
      if (props.onFormSubmit) {
        setIsLoading(true);
        if (isAddAlarm) {
          await props.onFormSubmit({ ...cleanedFormData, Units: null });
        } else {
          await props.onFormSubmit(cleanedFormData);
        }
        setIsLoading(false);
        setFormData(cleanedFormData);
      }
    } else {
      setFormData({ TagType: isAddAlarm ? typeCodeMap.alarm : typeCodeMap.standard });
      handleClose();
    }
  };

  return (
    <div>
      <DupontDialog
        open
        title={isAddAlarm ? AlarmPageStrings.addAlarm : TagPageStrings.addTag}
        subTitle={
          isAddAlarm ? AlarmPageStrings.addAlarmDescription.replace("[%]", systemName) : TagPageStrings.editTagDescription.replace("[%]", systemName)
        }
        showisRequired
        cancelBtnText={valueStrings.cancel}
        confirmBtnText={valueStrings.save}
        onClose={onFormSubmit}
        isOverlayLoader={isLoading}
        confirmDisabled={confirmDisabled}
      >
        <div className="form-content">
          <div className="columns">
            <div className="column">
              <InputBox
                labelText={isAddAlarm ? `${ALARMS_TABLE_STRINGS.headerAlarmID}*` : `${TagPageStrings.tagId}*`}
                name="TagId"
                requiredMsg={InputErrorStrings.required}
                value={formData.TagId || ""}
                onChange={onInputChange}
                placeholder={isAddAlarm ? AlarmPageStrings.alarmNameExample : TagPageStrings.tagIdExample}
                maxLength={20}
                errorMsg={tagIdError}
                onMaxLengthReached={onMaxLengthReachedTagId}
              />
              <div className="margin-top-20">
                <InputBox
                  labelText={`${TagPageStrings.tagDescription}*`}
                  name="Description"
                  requiredMsg={InputErrorStrings.required}
                  value={formData.Description || ""}
                  placeholder={isAddAlarm ? AlarmPageStrings.alarmDescriptionExample : TagPageStrings.tagDescriptionExample}
                  onChange={onInputChange}
                  maxLength={200}
                  errorMsg={tagDescriptionError}
                  onMaxLengthReached={onMaxLengthReachedDescription}
                />
              </div>
            </div>
            <div className="column">
              <InputBox
                labelText={isAddAlarm ? `${ALARMS_TABLE_STRINGS.headerAlarmName}*` : `${TagPageStrings.tagName}*`}
                name="TagName"
                value={formData.TagName || ""}
                requiredMsg={InputErrorStrings.required}
                maxLength={100}
                errorMsg={tagNameError}
                onMaxLengthReached={onMaxLengthReachedTag}
                placeholder={isAddAlarm ? AlarmPageStrings.alarmNameExample : TagPageStrings.tagNameExample}
                onChange={onInputChange}
              />
              {!isAddAlarm && (
                <div className="margin-top-20">
                  <InputBox
                    labelText={`${TagPageStrings.tagUnit}*`}
                    name="Units"
                    value={formData.Units || ""}
                    requiredMsg={InputErrorStrings.required}
                    onChange={onInputChange}
                    maxLength={20}
                    placeholder={TagPageStrings.tagUnitExample}
                    errorMsg={unitError}
                    onMaxLengthReached={onMaxLengthReachedUnit}
                  />
                </div>
              )}
            </div>
          </div>
          <div>
            {!isAddAlarm && (
              <div className="radio-group">
                <div className="tag-type-header">{Tag_Type_Strings.tagType}*</div>
                <div className="radios">
                  {tagTypeOptions.map(option => (
                    <label className="radio-label-font radiocontainer" key={option.value}>
                      {option.label}
                      <input
                        className="radioLabel"
                        type="radio"
                        value={option.value}
                        name="type"
                        checked={radioValue === option.value}
                        onChange={() => handleRadioChange(option.value)}
                      />
                      <span className="radioButton" />
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </DupontDialog>
    </div>
  );
};

export default AddTagAlarmModal;
